import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable, DataTableColumn, DataTableFilter } from '../components/ui/data-table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { applicationsAPI, directionsAPI, usersAPI } from '../lib/firestore';
import { functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../components/ui/use-toast';
import { MultiSelect } from '../components/ui/multi-select';
import type { Application, Direction, User } from '../types';
import Papa from 'papaparse';

export const ApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [directions, setDirections] = useState<Direction[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReviewEligibleOnly, setShowReviewEligibleOnly] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [formData, setFormData] = useState<Partial<Application>>({
    name: '',
    url: '',
    direction: '',
    admins: [],
    description: '',
    status: 'active',
    authType: 'local',
    twoFARequired: false,
    type: '',
    internetAccessible: false,
    backup: false,
    securityWarning: '',
    comments: '',
    vaultStorage: '',
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const { toast } = useToast();

  // Helper function to get user trigram from email
  const getUserTrigram = (email: string): string => {
    const user = users.find((u) => u.email === email);
    if (user) {
      const names = user.fullName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1].substring(0, 2)}`.toUpperCase();
      }
      return user.fullName.substring(0, 3).toUpperCase();
    }
    // Fallback to email prefix
    return email.split('@')[0].substring(0, 3).toUpperCase();
  };

  useEffect(() => {
    loadApplications();
    loadDirections();
    loadUsers();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await applicationsAPI.getAll();
      setApplications(data);
    } catch (error) {
      console.error('Error loading applications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load applications',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDirections = async () => {
    try {
      const data = await directionsAPI.getAll();
      setDirections(data);
    } catch (error) {
      console.error('Error loading directions:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getActive();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await applicationsAPI.delete(id);
        await loadApplications();
        toast({
          title: 'Success',
          description: 'Application deleted successfully',
          className: 'bg-green-50 border-green-200',
        });
      } catch (error) {
        console.error('Error deleting application:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete application',
          className: 'bg-red-50 border-red-200',
        });
      }
    }
  };

  const handleAdd = async () => {
    try {
      await applicationsAPI.create(formData);
      setIsAddDialogOpen(false);
      resetForm();
      await loadApplications();
      toast({
        title: 'Success',
        description: 'Application added successfully',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error adding application:', error);
      toast({
        title: 'Error',
        description: 'Failed to add application',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const handleEdit = async () => {
    if (!editingApp) return;
    try {
      await applicationsAPI.update(editingApp.id, formData);
      setIsEditDialogOpen(false);
      setEditingApp(null);
      resetForm();
      await loadApplications();
      toast({
        title: 'Success',
        description: 'Application updated successfully',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error updating application:', error);
      toast({
        title: 'Error',
        description: 'Failed to update application',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const openEditDialog = (app: Application) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      url: app.url || '',
      direction: app.direction,
      admins: app.admins,
      description: app.description || '',
      status: app.status,
      authType: app.authType,
      twoFARequired: app.twoFARequired,
      type: app.type || '',
      internetAccessible: app.internetAccessible,
      backup: app.backup,
      securityWarning: app.securityWarning || '',
      comments: app.comments || '',
      vaultStorage: app.vaultStorage || '',
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      direction: '',
      admins: [],
      description: '',
      status: 'active',
      authType: 'local',
      twoFARequired: false,
      type: '',
      internetAccessible: false,
      backup: false,
      securityWarning: '',
      comments: '',
      vaultStorage: '',
    });
  };

  const handleCSVImport = async () => {
    if (!csvFile) return;

    try {
      setImporting(true);
      const text = await csvFile.text();

      const importFunction = httpsCallable(functions, 'importApplicationsFromCSV');
      const result = await importFunction({ csvContent: text }) as any;

      toast({
        title: 'Import Complete',
        description: `Imported ${result.data.successCount} applications. ${result.data.errorCount} errors.`,
        className: 'bg-green-50 border-green-200',
      });

      setIsImportDialogOpen(false);
      setCsvFile(null);
      await loadApplications();
    } catch (error: any) {
      console.error('Error importing CSV:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import CSV',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExportCSV = () => {
    const csv = Papa.unparse(applications);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `applications-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter applications for review eligibility
  const displayedApplications = showReviewEligibleOnly
    ? applications.filter(
        (app) =>
          app.status === 'active' &&
          (app.authType === 'local' || app.authType === 'other') &&
          app.internetAccessible === true
      )
    : applications;

  const applicationFormFields = (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Application name"
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="url">URL</Label>
        <Input
          id="url"
          type="text"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://example.com"
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="direction">Direction</Label>
        <Select
          value={formData.direction}
          onValueChange={(value) => setFormData({ ...formData, direction: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a direction" />
          </SelectTrigger>
          <SelectContent>
            {directions.map((direction) => (
              <SelectItem key={direction.id} value={direction.name}>
                {direction.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="admins">Admins</Label>
        <MultiSelect
          options={users.map((user) => ({
            value: user.email,
            label: `${user.fullName} (${user.email})`,
          }))}
          selected={formData.admins || []}
          onChange={(selected) => setFormData({ ...formData, admins: selected })}
          placeholder="Select admins..."
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Application description"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={formData.status}
            onValueChange={(value: any) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="deprecated">Deprecated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="authType">Auth Type</Label>
          <Select
            value={formData.authType}
            onValueChange={(value: any) => setFormData({ ...formData, authType: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select auth type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sso">SSO</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="ldap">LDAP</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="twoFARequired"
          checked={formData.twoFARequired}
          onChange={(e) => setFormData({ ...formData, twoFARequired: e.target.checked })}
          className="rounded border-gray-300"
        />
        <Label htmlFor="twoFARequired">2FA Required</Label>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="internetAccessible"
          checked={formData.internetAccessible}
          onChange={(e) => setFormData({ ...formData, internetAccessible: e.target.checked })}
          className="rounded border-gray-300"
        />
        <Label htmlFor="internetAccessible">Internet Accessible</Label>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="backup"
          checked={formData.backup}
          onChange={(e) => setFormData({ ...formData, backup: e.target.checked })}
          className="rounded border-gray-300"
        />
        <Label htmlFor="backup">Backup Enabled</Label>
      </div>
    </div>
  );

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Applications</h1>
          <div className="space-x-2">
            <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
              Import CSV
            </Button>
            <Button variant="outline" onClick={handleExportCSV}>
              Export CSV
            </Button>
            <Button onClick={() => setIsAddDialogOpen(true)}>Add Application</Button>
          </div>
        </div>

        {/* Quick Filter for Review Eligible Apps */}
        <div className="flex items-center gap-2">
          <Button
            variant={showReviewEligibleOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowReviewEligibleOnly(!showReviewEligibleOnly)}
          >
            {showReviewEligibleOnly ? '✓ ' : ''}Review Eligible Apps
          </Button>
          {showReviewEligibleOnly && (
            <span className="text-sm text-muted-foreground">
              (Active, Local/Other auth, Internet accessible)
            </span>
          )}
        </div>

{loading ? (
          <div>Loading applications...</div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                {showReviewEligibleOnly ? 'Review Eligible Applications' : 'All Applications'} (
                {displayedApplications.length}
                {showReviewEligibleOnly && ` of ${applications.length}`})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={displayedApplications}
                columns={[
                  {
                    key: 'name',
                    label: 'Name',
                    sortable: true,
                    render: (app) => <span className="font-medium">{app.name}</span>,
                  },
                  {
                    key: 'direction',
                    label: 'Direction',
                    sortable: true,
                  },
                  {
                    key: 'admins',
                    label: 'Admins',
                    render: (app) => (
                      <div className="flex gap-1 flex-wrap">
                        {app.admins.slice(0, 3).map((email) => {
                          const user = users.find((u) => u.email === email);
                          return (
                            <Badge
                              key={email}
                              variant="outline"
                              className="text-xs font-mono"
                              title={user?.fullName || email}
                            >
                              {getUserTrigram(email)}
                            </Badge>
                          );
                        })}
                        {app.admins.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{app.admins.length - 3}
                          </Badge>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'internetAccessible',
                    label: 'Internet',
                    sortable: true,
                    render: (app) =>
                      app.internetAccessible ? (
                        <span className="text-indigo-600 dark:text-indigo-400" title="Accessible depuis Internet">🌐</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500" title="Réseau interne uniquement">🔒</span>
                      ),
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    render: (app) => (
                      <Badge
                        className={
                          app.status === 'active'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30'
                            : app.status === 'deprecated'
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                      >
                        {app.status}
                      </Badge>
                    ),
                  },
                  {
                    key: 'authType',
                    label: 'Auth Type',
                    sortable: true,
                    render: (app) => (
                      <Badge variant="outline">{app.authType.toUpperCase()}</Badge>
                    ),
                  },
                  {
                    key: 'twoFARequired',
                    label: '2FA',
                    sortable: true,
                    render: (app) =>
                      app.twoFARequired ? (
                        <span className="text-green-600 dark:text-green-400">✓</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">✗</span>
                      ),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (app) => (
                      <div className="space-x-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(app)}>
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(app.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    ),
                  },
                ]}
                searchKeys={['name', 'direction', 'admins']}
                filters={[
                  {
                    key: 'status',
                    label: 'Status',
                    options: [
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                      { value: 'deprecated', label: 'Deprecated' },
                    ],
                  },
                  {
                    key: 'authType',
                    label: 'Auth Type',
                    options: [
                      { value: 'sso', label: 'SSO' },
                      { value: 'local', label: 'Local' },
                      { value: 'ldap', label: 'LDAP' },
                      { value: 'other', label: 'Other' },
                    ],
                  },
                ]}
                emptyMessage="No applications found. Add your first application or import from CSV."
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Application</DialogTitle>
            <DialogDescription>
              Enter the details of the new application.
            </DialogDescription>
          </DialogHeader>
          {applicationFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd}>Add Application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>
              Update the application details.
            </DialogDescription>
          </DialogHeader>
          {applicationFormFields}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Applications from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with application data. The CSV should match the inventory format.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCSVImport} disabled={!csvFile || importing}>
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
